import { useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Stack,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardContent,
    CardActions,
    Divider,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import apiRequest from "../../../apiRequest";

import PageLoader from "../../../components/PageLoader/PageLoader";
import AppSnackbar from "../../../components/AppSnackbar/AppSnackbar";
import ConfirmDialog from "components/ConfirmDialog/ConfirmDialog";
import ImagePreview from "components/ImagePreview/ImagePreview";

const VehicleViewModal = ({ open, onClose, data, actions, onRefresh }) => {
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");


    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleConfirmApprove = async () => {
        setConfirmOpen(false);
        setLoading(true);

        const payload = {
            id: data.id,
            status: "approved",
            reason: "Approved by Admin",
        };

        try {
            const res = await apiRequest.put(
                "/v1/admin/driver/vehicle/status",
                payload
            );

            if (res?.success) {
                showSnackbar("Approved successfully", "success");
                onRefresh?.();
                onClose?.();
            } else {
                showSnackbar(res?.message || "Approval failed", "error");
            }
        } catch (error) {
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleActionClick = async (action) => {
        const label = action.label;


        if (label === "rejected" || label === "request_info") {
            setSelectedAction(action);
            setReason("");
            setReasonDialogOpen(true);
            return;
        }


        if (label === "approved") {
            setConfirmData({ id: data.id, action: label });
            setConfirmOpen(true);

        }
    };

    const handleSubmitReason = async () => {
        if (!selectedAction) return;
        setLoading(true);

        const payload = {
            id: data.id,
            status: selectedAction.label,
            reason: reason.trim(),
        };

        try {
            const res = await apiRequest.put(
                "/v1/admin/driver/vehicle/status",
                payload
            );
            if (res?.success) {
                showSnackbar(`${selectedAction.label} successfully`, "success");
                setReasonDialogOpen(false);
                onRefresh?.();
                onClose?.();
            } else {
                showSnackbar(res?.message || "Action failed", "error");
            }
        } catch (error) {
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
            setSelectedAction(null);
            setReason("");
        }
    };

    const getButtonTextColor = (label) => {
        switch (label.toLowerCase()) {
            case "approved":
                return "#2e7d32";
            case "rejected":
                return "#d32f2f";
            case "request_info":
                return "#ed6c02";
            default:
                return "#1976d2";
        }
    };

    const getButtonHoverColor = (label) => {
        switch (label.toLowerCase()) {
            case "approved":
                return "#1b5e20";
            case "rejected":
                return "#b71c1c";
            case "request_info":
                return "#e65100";
            default:
                return "#115293";
        }
    };

    const infoCards = [
        { label: "Number Plate", value: data?.number_plate },
        { label: "Vehicle Make", value: data?.vehicle_make },
        { label: "Vehicle Model", value: data?.vehicle_model },
        { label: "Vehicle Color", value: data?.vehicle_color },
        { label: "Vehicle Year", value: data?.vehicle_year },
        { label: "Registration Number", value: data?.registration_number },
        {
            label: "Insurance Expiration",
            value: data?.insurance_expiration
                ? new Date(data.insurance_expiration).toLocaleDateString()
                : "-",
        },
        ...(data?.status !== "approved" ? [{ label: "Reason for Rejection", value: data?.reason_for_rejection }] : []),
        { label: "Category", value: data?.category?.name_en },
        { label: "Sub Category", value: data?.sub_category?.name_en },
        { label: "Tow Operator Certificate", value: data?.tow_operator_certificate ? "Provided" : "-" },
    ];

    const documentCards = [
        { label: "Insurance Photo", key: "insurance_photo" },
        { label: "Ownership Photo", key: "ownership_photo" },
        { label: "Tow Operator Certificate", key: "tow_operator_certificate" },
    ];

    // const renderDoc = (key) => {
    //     const src = data?.[key];
    //     if (!src) return "-";
    //     return (
    //         <Box
    //             component="img"
    //             src={`${apiRequest.BASE_URL}/${src}`}
    //             sx={{
    //                 width: "100%",
    //                 maxWidth: 200,
    //                 borderRadius: 2,
    //                 border: "1px solid #e5e7eb",
    //                 boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
    //             }}
    //         />
    //     );
    // };

    return (
        <>
            {/* MAIN MODAL */}
            <Modal open={open} onClose={onClose}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 780,
                        height: "90vh",
                        maxHeight: "90vh",
                        overflow: "hidden",
                    }}
                >
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: 8,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <IconButton
                            onClick={onClose}
                            sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {data && (
                            <>
                                <CardContent
                                    sx={{
                                        p: 0,
                                        flex: 1,
                                        overflowY: "auto",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 4,
                                            py: 3,
                                            background: "linear-gradient(135deg, #d3f8df 0%, #f4fff7 100%)",
                                            borderBottom: "1px solid #e5e7eb",
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Vehicle Details
                                        </Typography>
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            spacing={2}
                                            mt={1}
                                        >
                                            <Box>
                                                <Typography variant="h5" fontWeight={700}>
                                                    {data.vehicle_make} {data.vehicle_model}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Plate #{data.number_plate || "-"}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1}>
                                                <Chip
                                                    label={data.status || "Pending"}
                                                    color={
                                                        data.status === "approved"
                                                            ? "success"
                                                            : data.status === "rejected"
                                                                ? "error"
                                                                : "warning"
                                                    }
                                                />
                                                <Chip label={`Year ${data.vehicle_year || "-"}`} variant="outlined" />
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ px: 4, py: 3 }}>
                                        <Grid container spacing={2}>
                                            {infoCards.map((item) => (
                                                <Grid item xs={12} sm={6} key={item.label}>
                                                    <Box
                                                        sx={{
                                                            border: "1px solid #edf2f7",
                                                            borderRadius: 2,
                                                            p: 2,
                                                            backgroundColor: "#fafafa",
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.label}
                                                        </Typography>
                                                        <Typography fontWeight={600}>
                                                            {item.value || "-"}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <Divider sx={{ my: 3 }} />

                                        <Typography variant="subtitle2" mb={2}>
                                            Documents
                                        </Typography>


                                        <Grid container spacing={2}>
                                            {documentCards.map((doc) => (
                                                <Grid item xs={12} sm={4} key={doc.label}>
                                                    <Box
                                                        sx={{
                                                            border: "1px solid #e5e7eb",
                                                            borderRadius: 2,
                                                            p: 2,
                                                            backgroundColor: "#fff",
                                                            minHeight: 200,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Stack spacing={1} alignItems="center">
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                textAlign="center"
                                                            >
                                                                {doc.label}
                                                            </Typography>

                                                            {/* Render Image or Placeholder */}
                                                            {data?.[doc.key] ? (
                                                                <Box
                                                                    component="img"
                                                                    src={`${apiRequest.BASE_URL}/${data[doc.key]}`}
                                                                    sx={{
                                                                        width: "100%",
                                                                        maxWidth: 200,
                                                                        borderRadius: 2,
                                                                        border: "1px solid #e5e7eb",
                                                                        boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                                                                        cursor: "pointer",
                                                                    }}
                                                                    onClick={() => {
                                                                        setPreviewImage(`${apiRequest.BASE_URL}/${data[doc.key]}`);
                                                                        setPreviewOpen(true);
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography color="text.secondary" fontStyle="italic">
                                                                    No Image
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <Box pb={3} />
                                    </Box>
                                </CardContent>
                                <CardActions
                                    sx={{
                                        borderTop: "1px solid #e5e7eb",
                                        p: 3,
                                        justifyContent: "flex-end",
                                        gap: 1.5,
                                        flexWrap: "wrap",
                                        bgcolor: "#fff",
                                    }}
                                >
                                    {actions.map((action) => (
                                        <Button
                                            key={action.label}
                                            variant="outlined"
                                            onClick={() => handleActionClick(action)}
                                            sx={{
                                                minWidth: 140,
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                color: getButtonTextColor(action.label),
                                                borderColor: getButtonTextColor(action.label),
                                                "&:hover": {
                                                    bgcolor: "#f5f5f5",
                                                    borderColor: getButtonHoverColor(action.label),
                                                },
                                            }}
                                        >
                                            {action.label.replace("_", " ").toUpperCase()}
                                        </Button>
                                    ))}
                                </CardActions>
                            </>
                        )}
                    </Card>
                </Box>
            </Modal>

            {/* REASON DIALOG */}
            <Dialog open={reasonDialogOpen} onClose={() => setReasonDialogOpen(false)}>
                <DialogTitle>{selectedAction?.label}</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{
                            borderColor: "#2e7d32",
                            color: "#fcfcfcff",
                        }}
                        disabled={!reason.trim()}
                        onClick={handleSubmitReason}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CONFIRM DIALOG */}
            <ConfirmDialog
                open={confirmOpen}
                title="Approve Vehicle"
                description={`Are you sure you want to approve this ${data?.vehicle_make} ${data?.vehicle_model}?`}
                confirmText="Yes, Approve"
                confirmColor="success"
                loading={loading}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmApprove}
            />

            <ImagePreview
                open={previewOpen}
                imageSrc={previewImage}
                onClose={() => setPreviewOpen(false)}
            />
            {/* GLOBAL LOADER */}
            <PageLoader open={loading} />
            <AppSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default VehicleViewModal;
